import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fmtMoney, fmtDateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Wallet as WalletIcon, Plus, Loader2, ArrowDownLeft, ArrowUpRight } from "lucide-react";

const TX_STYLE = {
  topup: { label: "Top-up", cls: "bg-[hsl(210,30%,92%)] text-[hsl(210,35%,22%)]" },
  payment: { label: "Licence payment", cls: "bg-[hsl(6,62%,92%)] text-[hsl(6,55%,22%)]" },
  payout: { label: "Upfront payout", cls: "bg-[hsl(174,38%,92%)] text-[hsl(174,45%,18%)]" },
};

export default function Wallet() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/wallet");
      setData(d);
    } catch (e) {
      toast.error(errMsg(e, "Could not load wallet"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const topup = async (amt) => {
    const value = parseFloat(amt);
    if (!value || value <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    setBusy(true);
    try {
      await api.post("/wallet/topup", { amount: value });
      toast.success(`${fmtMoney(value)} added to your wallet`);
      setAmount("");
      await load();
    } catch (e) {
      toast.error(errMsg(e, "Top-up failed"));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="page-container space-y-6 py-10">
        <Skeleton className="h-32 rounded-2xl" />
        <Skeleton className="h-64 rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="page-container max-w-4xl py-8 sm:py-12">
      <h1 className="font-display text-3xl tracking-[-0.01em] sm:text-4xl">Wallet</h1>
      <p className="mt-1 text-sm text-muted-foreground">Simulated escrow wallet — top up, pay for licences, receive payouts.</p>

      <div className="mt-8 grid gap-6 sm:grid-cols-2">
        <Card className="rounded-2xl border-2 border-[hsl(174,38%,70%)]">
          <CardContent className="space-y-1 p-6">
            <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <WalletIcon className="h-3.5 w-3.5" /> Available balance
            </p>
            <p data-testid="wallet-balance" className="font-display text-5xl">{fmtMoney(data?.balance)}</p>
            <p className="font-mono-plex text-xs text-muted-foreground">USD · simulated funds</p>
          </CardContent>
        </Card>
        <Card className="rounded-2xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Top up</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex gap-2">
              {[500, 1000, 5000].map((v) => (
                <Button key={v} data-testid={`wallet-quick-topup-${v}`} variant="outline" disabled={busy}
                  className="flex-1 rounded-xl border bg-card hover:bg-muted" onClick={() => topup(v)}>
                  +{fmtMoney(v)}
                </Button>
              ))}
            </div>
            <div className="flex gap-2">
              <Input data-testid="wallet-topup-amount-input" type="number" min="1" placeholder="Custom amount"
                value={amount} onChange={(e) => setAmount(e.target.value)} className="rounded-xl bg-card" />
              <Button data-testid="wallet-topup-button" disabled={busy} onClick={() => topup(amount)} className="rounded-xl">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                <span className="ml-1">Add</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="mt-6 rounded-2xl">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Transactions</CardTitle>
        </CardHeader>
        <CardContent>
          {data?.transactions?.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">No transactions yet.</p>
          ) : (
            <Table data-testid="wallet-transactions-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Reference</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.transactions.map((tx) => {
                  const meta = TX_STYLE[tx.type] || { label: tx.type, cls: "" };
                  const positive = tx.amount >= 0;
                  return (
                    <TableRow key={tx.id}>
                      <TableCell>
                        <Badge variant="outline" className={`rounded-full text-[10px] ${meta.cls}`}>{meta.label}</Badge>
                      </TableCell>
                      <TableCell className="max-w-[220px] truncate text-sm">{tx.description}</TableCell>
                      <TableCell className="font-mono-plex text-xs text-muted-foreground">{tx.ref}</TableCell>
                      <TableCell className="font-mono-plex text-xs text-muted-foreground">{fmtDateTime(tx.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <span className={`inline-flex items-center gap-1 font-semibold ${positive ? "text-[hsl(174,45%,25%)]" : "text-destructive"}`}>
                          {positive ? <ArrowDownLeft className="h-3.5 w-3.5" /> : <ArrowUpRight className="h-3.5 w-3.5" />}
                          {positive ? "+" : "−"}{fmtMoney(Math.abs(tx.amount))}
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
